import React from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Compass, Heart, CheckCircle2, Info } from 'lucide-react';
import { backofficeApi, formatNumber } from '@kardume/services';
import { theme } from '@kardume/theme';
import type { MerchantVisitIntentRankDto } from '@kardume/types';
import {
  Card,
  CardHeader,
  CardBody,
  StatCard,
  Table,
  TableContainer,
  Badge,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from './DataState';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Note = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 1rem 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const Empty = styled.p`
  margin: 0;
  color: ${(p) => p.theme.textMuted};
  font-size: 0.9rem;
`;

/** Quantas categorias cabem no gráfico antes de os rótulos virarem um borrão. */
const MAX_CATEGORIES = 8;

/**
 * Lojas com fila de interesse e pouca conversão - a lista comercial.
 *
 * A coluna "não foram" é a diferença entre quem quer ir e quem foi, e é ela que
 * ordena a leitura: uma loja com 200 interessados e 190 visitas não precisa de
 * campanha; uma com 60 interessados e 3 visitas precisa. Sem essa coluna, o
 * ranking por "quero conhecer" premiaria a loja grande, que é a que menos
 * precisa de ajuda.
 */
const RankTable: React.FC<{ rows: MerchantVisitIntentRankDto[]; showGap: boolean }> = ({
  rows,
  showGap,
}) => {
  if (rows.length === 0) {
    return <Empty>Sem dado suficiente para montar o ranking ainda.</Empty>;
  }

  return (
    <TableContainer>
      <Table>
        <thead>
          <tr>
            <th>Loja</th>
            <th>Querem ir</th>
            <th>Já foram</th>
            {showGap && <th>Não foram</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.merchantId}>
              <td>{row.merchantName}</td>
              <td>{formatNumber(row.counts.wantToGo)}</td>
              <td>{formatNumber(row.counts.visited)}</td>
              {showGap && (
                <td>
                  {/* Só destaca quando há fila de fato: com zero, o `warning`
                      pintaria de laranja a loja que já converteu tudo. */}
                  <Badge tone={row.counts.wantToGo - row.counts.visited > 0 ? 'warning' : 'secondary'}>
                    {formatNumber(Math.max(0, row.counts.wantToGo - row.counts.visited))}
                  </Badge>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  );
};

/**
 * Intenção de visita da plataforma (card 214).
 *
 * Carrega separado do painel: exige `ROLE_BACKOFFICE_REPORT_READ`, e quem não
 * tem a permissão precisa continuar vendo o resto da página de relatórios em
 * vez de bater num erro que engole tudo.
 *
 * **Só agrega.** Não existe caminho, aqui nem no core, que devolva quem salvou
 * qual loja - nem para a operação. A ausência é a decisão, não uma pendência.
 */
const VisitIntentReport: React.FC = () => {
  const { data, loading, error, reload } = useAsync(() => backofficeApi.getVisitIntentReport(), []);

  const categorias = (data?.byCategory ?? []).slice(0, MAX_CATEGORIES).map((row) => ({
    categoria: row.categoryName,
    'Querem ir': row.counts.wantToGo,
    Favoritas: row.counts.favorite,
    'Já foram': row.counts.visited,
  }));

  return (
    <Card>
      <CardHeader>
        <h3>Intenção de visita</h3>
      </CardHeader>
      <CardBody>
        <DataState loading={loading} error={error} onRetry={reload}>
          {data && (
            <>
              <Grid>
                <StatCard
                  label="Querem conhecer"
                  value={formatNumber(data.platform.wantToGo)}
                  icon={<Compass size={18} />}
                  iconColor={theme.info}
                />
                <StatCard
                  label="Favoritadas"
                  value={formatNumber(data.platform.favorite)}
                  icon={<Heart size={18} />}
                  iconColor={theme.accent}
                />
                <StatCard
                  label="Visitas registradas"
                  value={formatNumber(data.platform.visited)}
                  icon={<CheckCircle2 size={18} />}
                  iconColor={theme.success}
                />
              </Grid>

              <Split>
                <Card>
                  <CardHeader>
                    <h3>Mais desejadas</h3>
                  </CardHeader>
                  <CardBody>
                    <RankTable rows={data.topWantToGo} showGap />
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <h3>Mais visitadas</h3>
                  </CardHeader>
                  <CardBody>
                    <RankTable rows={data.topVisited} showGap={false} />
                  </CardBody>
                </Card>
              </Split>

              {categorias.length > 0 && (
                <>
                  <CardHeader>
                    <h3>Interesse por categoria</h3>
                  </CardHeader>
                  {/* Barras agrupadas, e não empilhadas: empilhado mostra o
                      volume total da categoria, e a pergunta aqui é outra -
                      onde a distância entre querer ir e ir é maior. */}
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={categorias}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="categoria" tick={{ fontSize: 12, fill: theme.textMuted }} />
                      <YAxis tick={{ fontSize: 12, fill: theme.textMuted }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Querem ir" fill={theme.info} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Favoritas" fill={theme.accent} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Já foram" fill={theme.success} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}

              <Note>
                <Info size={15} />
                <span>
                  <strong>&quot;Já foram&quot;</strong> conta apenas visita comprovada por cupom
                  validado, mais quem marcou a visita à mão no aplicativo - a plataforma não enxerga
                  quem entrou na loja sem usar cupom, então o número real é maior.{' '}
                  <strong>&quot;Não foram&quot;</strong> é a fila de interesse ainda não convertida:
                  é a lista de quem tem público esperando e precisa de campanha. Todos os números
                  são agregados; o relatório não expõe quem salvou qual loja.
                </span>
              </Note>
            </>
          )}
        </DataState>
      </CardBody>
    </Card>
  );
};

export default VisitIntentReport;
